/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SpeedDialsActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for Speed Dials data grid rows.
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Edit, Copy, Trash2 } from 'lucide-react';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../hooks/useI18n';
import type { SpeedDial } from '../api/speedDialsApi';

interface SpeedDialsActionsProps {
  item: SpeedDial;
  onEdit: (item: SpeedDial) => void;
  onDuplicate: (item: SpeedDial) => void;
  onDelete: (item: SpeedDial) => void;
}

export default function SpeedDialsActions({
  item,
  onEdit,
  onDuplicate,
  onDelete
}: SpeedDialsActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-1.5">
      <SimpleTooltip content={t('common.edit')} position="top">
        <Button
          type="button"
          onClick={() => onEdit(item)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          aria-label={t('common.edit')}
          variant="ghost"
          size="sm"
        >
          <Edit className="h-4 w-4" />
        </Button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <Button
          type="button"
          onClick={() => onDuplicate(item)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
          aria-label={t('common.duplicate')}
          variant="ghost"
          size="sm"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <Button
          type="button"
          onClick={() => onDelete(item)}
          className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          aria-label={t('common.delete')}
          variant="ghost"
          size="sm"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </SimpleTooltip>
    </div>
  );
}

